import { create } from 'zustand';
import i18n, { type Lang } from '../i18n/i18n';

// UI-only state (skill aerostores-frontend §3): language, sidebar drawer, selected printer.
// The language preference is not security-relevant, so it may live in localStorage (design-check --lang ar sets it).
const LANG_KEY = 'lang';

export const hasStoredLang = (): boolean => {
  try {
    return localStorage.getItem(LANG_KEY) !== null;
  } catch {
    return false;
  }
};

const readLang = (): Lang => {
  try {
    return localStorage.getItem(LANG_KEY) === 'ar' ? 'ar' : 'en';
  } catch {
    return 'en';
  }
};

/** Applies a language to the document (dir + lang), i18next and localStorage. AntD direction follows the store in App.tsx. */
export const applyLang = (lang: Lang): void => {
  document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
  document.documentElement.lang = lang;
  void i18n.changeLanguage(lang);
  try {
    localStorage.setItem(LANG_KEY, lang);
  } catch {
    /* private mode — keep in memory only */
  }
};

interface UiState {
  lang: Lang;
  sidebarOpen: boolean;
  /** Ctrl+K — the way past eleven collapsed nav groups when you already know the screen you want. */
  paletteOpen: boolean;
  printer: string | null;
  setLang: (lang: Lang) => void;
  toggleSidebar: () => void;
  setPaletteOpen: (open: boolean) => void;
  closeSidebar: () => void;
  setPrinter: (printer: string | null) => void;
}

export const useUi = create<UiState>()((set) => ({
  lang: readLang(),
  sidebarOpen: false,
  paletteOpen: false,
  printer: null,
  setLang: (lang) => {
    applyLang(lang);
    set({ lang });
  },
  toggleSidebar: () => {
    set((s) => ({ sidebarOpen: !s.sidebarOpen }));
  },
  closeSidebar: () => {
    set({ sidebarOpen: false });
  },
  setPaletteOpen: (paletteOpen) => {
    set({ paletteOpen });
  },
  setPrinter: (printer) => {
    set({ printer });
  },
}));
